import { type } from 'arktype';
import { minutesToTime, timeToMinutes } from './time.utils';

export const OVERLAP_POLICIES = ['allow', 'warn', 'conflict'] as const;
export type OverlapPolicy = (typeof OVERLAP_POLICIES)[number];

export const GENERIC_SLOT_TYPE_KEY = 'untyped';

export const MAX_SLOT_MARGIN_MINUTES = 720;

export const MINUTES_PER_DAY = 24 * 60;

export const END_OF_DAY_TIME = '24:00';

const OVERLAP_POLICY_RANK: Record<OverlapPolicy, number> = { allow: 0, warn: 1, conflict: 2 };

const overlapPolicySchema = type("'allow'|'warn'|'conflict'");

const marginMinutesSchema = type(`0 <= number.integer <= ${MAX_SLOT_MARGIN_MINUTES}`);

const overlapRulesSchema = type({
  'default?': overlapPolicySchema.configure({
    description: 'Policy against any entry type without a `byType` rule. Absent means `allow`.',
  }),
  'byType?': type({ '[string]': overlapPolicySchema }).configure({
    description: `Policy per overlapping entry type key; \`${GENERIC_SLOT_TYPE_KEY}\` addresses entries without a slot type. The strictest rule of both sides wins.`,
    examples: [{ 'external-booking': 'conflict', [GENERIC_SLOT_TYPE_KEY]: 'warn' }],
  }),
}).configure({ description: 'Overlap rules against other entries in the same calendar. Absent means `allow`.' });

const briefMinutesSchema = marginMinutesSchema.configure({
  description: 'Preparation time blocked before the entry; counts for overlap checks. Default 0.',
});

const debriefMinutesSchema = marginMinutesSchema.configure({
  description: 'Wrap-up time blocked after the entry; counts for overlap checks. Default 0.',
});

export const slotTypeSchedulingConfigSchema = type({
  'overlap?': overlapRulesSchema,
  'briefMinutes?': briefMinutesSchema,
  'debriefMinutes?': debriefMinutesSchema,
}).configure({ description: 'Scheduling keys of the slot type `config`.' });
export type SlotTypeSchedulingConfig = typeof slotTypeSchedulingConfigSchema.infer;

export interface SlotTypeMargins {
  briefMinutes: number;
  debriefMinutes: number;
}

interface OverlapSide {
  type?: string | null;
  config?: Record<string, unknown>;
}

interface SlotTimeRange {
  startTime: string;
  endTime: string;
}

interface EffectiveSlotRange {
  effectiveStartTime: string;
  effectiveEndTime: string;
}

function validOrUndefined<T>(parse: (value: unknown) => T | type.errors, value: unknown): T | undefined {
  if (value === undefined) return undefined;
  const parsed = parse(value);
  return parsed instanceof type.errors ? undefined : parsed;
}

function schedulingConfigOf(config: Record<string, unknown> | undefined): SlotTypeSchedulingConfig {
  const overlap = validOrUndefined(overlapRulesSchema, config?.['overlap']);
  const briefMinutes = validOrUndefined(briefMinutesSchema, config?.['briefMinutes']);
  const debriefMinutes = validOrUndefined(debriefMinutesSchema, config?.['debriefMinutes']);
  return {
    ...(overlap && { overlap }),
    ...(briefMinutes !== undefined && { briefMinutes }),
    ...(debriefMinutes !== undefined && { debriefMinutes }),
  };
}

export function slotTypeKeyOf(slotType: string | null | undefined): string {
  return slotType?.trim() || GENERIC_SLOT_TYPE_KEY;
}

export function slotTypeMarginsOf(config: Record<string, unknown> | undefined): SlotTypeMargins {
  const { briefMinutes = 0, debriefMinutes = 0 } = schedulingConfigOf(config);
  return { briefMinutes, debriefMinutes };
}

function overlapPolicyTowards(side: OverlapSide, otherType: string | null | undefined): OverlapPolicy {
  const { overlap } = schedulingConfigOf(side.config);
  return overlap?.byType?.[slotTypeKeyOf(otherType)] ?? overlap?.default ?? 'allow';
}

export function resolveOverlapPolicy(a: OverlapSide, b: OverlapSide): OverlapPolicy {
  const policies = [overlapPolicyTowards(a, b.type), overlapPolicyTowards(b, a.type)];
  return policies.reduce((strictest, policy) => (OVERLAP_POLICY_RANK[policy] > OVERLAP_POLICY_RANK[strictest] ? policy : strictest));
}

export function effectiveSlotRange(range: SlotTimeRange, margins: SlotTypeMargins): EffectiveSlotRange {
  const start = timeToMinutes(range.startTime);
  const end = range.endTime === END_OF_DAY_TIME ? MINUTES_PER_DAY : timeToMinutes(range.endTime);
  if (start === null || end === null) return { effectiveStartTime: range.startTime, effectiveEndTime: range.endTime };
  return {
    effectiveStartTime: minutesToTime(Math.max(0, start - margins.briefMinutes)),
    effectiveEndTime: minutesToTime(Math.min(MINUTES_PER_DAY, end + margins.debriefMinutes)),
  };
}

export function slotTypeSupportsRecurrence(behaviorFlags: { supportsRecurrence?: boolean } | null | undefined): boolean {
  return behaviorFlags?.supportsRecurrence !== false;
}
